/* eslint-disable prefer-const */
import { useMediaQuery, useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { ApiActions } from '@orientaction/api-actions';
import { getFromLS, isAdmin, publicIconPath } from '@orientaction/utils';
import { kebabCase } from 'lodash';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import round from 'lodash/round';
import sumBy from 'lodash/sumBy';
import { FC, useEffect, useMemo, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Details from './Details';
import Diagram from './Diagram';
import ResultImage from './ResultImage';
import ShareIcons from './ShareIcons';
import style from './style';

interface IProps {
  userResponse: any;
  newVersion?: boolean;
  sendDataMax?: (data: any) => void;
}

const colors = [
  '#FDDA5D',
  '#F9BC5D',
  '#F18965',
  '#D45E6B',
  '#BB3570',
  '#8B2D90',
  '#602faa',
  '#414197',
];

const PieDiagramContainer: FC<IProps> = ({ userResponse, newVersion = false, sendDataMax }) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = style();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to strip HTML tags
  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  };

  let allVariables: any[] = [];
  userResponse?.attributes?.responses?.data.forEach((response: any) => {
    response?.attributes.variable.data.forEach((variable: any) => {
      allVariables.push({
        ...variable,
        variableValue: response.attributes.variableValue, // Attribuer variableValue à chaque variable
      });
    });
  });
  const variableGroup = groupBy(allVariables, (variable: any) => variable.id);

  /* const variableGroup = groupBy(userResponse?.attributes.responses.data, (response: any) => {
    return response.attributes.variable.data.id;
  }); */

  const dataMapValue = map(variableGroup, (variables: any[]) => {
    const sumVariable = sumBy(variables, (variable: any) => variable.variableValue);
    const firstVariable = variables[0];

    return {
      value: sumVariable,
      label: stripHtml(firstVariable.attributes.title),
      title: firstVariable.attributes.title,
      image: firstVariable.attributes.image,
      video: firstVariable.attributes.video,
      typeMedia: firstVariable.attributes.typeMedia,
      description: firstVariable.attributes.description,
    };
  });
  /* const dataMapValue = map(variableGroup, (response: any) => {
    const sumVariable = sumBy(response, (variable: any) => variable.attributes.variableValue);

    const dataBeforeRemove = {
      value: sumVariable,
      label: response[0]?.attributes?.variable?.data?.attributes?.name,
      title: response[0]?.attributes?.variable?.data?.attributes?.title,
      image: response[0]?.attributes?.variable?.data?.attributes?.image,
      description: response[0]?.attributes?.variable?.data?.attributes?.description,
    };
    return dataBeforeRemove;
  }); */

  // const totalValue = sumBy(dataMapValue, (value: any) => value.value) || 0;
  const totalValue = sumBy(dataMapValue, (item: any) => item.value) || 0;

  const dataMapValuePourcentage = dataMapValue
    .sort((a: any, b: any) => b.value - a.value)
    .map((item: any, index: number) => {
      const valuePercent = (item.value * 100) / totalValue;
      return {
        ...item,
        value: round(valuePercent, 2), // Arrondir le pourcentage
        color: colors[index % colors.length], // Affecter une couleur
      };
    });

  const maximumResponse = useMemo(() => dataMapValuePourcentage[0], [dataMapValuePourcentage]);

  // Send data to parent component
  useEffect(() => {
    if (sendDataMax && maximumResponse) {
      sendDataMax(maximumResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResponse]);

  const goToTestPage = () => {
    dispatch(ApiActions.setTabLayout(1 as any));
    history.push('/user/tests');
  };

  const copyTestLink = () => {
    navigator.clipboard.writeText(testUrl);
    setLinkCopied(true);
  };

  const test = userResponse?.attributes?.test?.data;
  const testUrl = `${process.env.REACT_APP_SHARE_ENDPOINT}/api/test/${test.id}/${kebabCase(
    test.attributes.resultTitle || test.attributes.title
  )}`;
  const user = JSON.parse(getFromLS('userInfos') || '');

  return (
    <>
      <div
        className={classes.resultPaperContainer}
        style={newVersion && !isMobile ? { marginTop: 0, marginBottom: 24 } : {}}>
        <Grid
          alignItems="center"
          className={classes.diagramContainer}
          container={true}
          style={newVersion && !isMobile ? { padding: 0 } : {}}>
          <Grid item={true} sm={12} md={12} lg={12} xl={12}>
            <Grid
              alignItems="center"
              container={true}
              style={
                newVersion && !isMobile
                  ? {
                      flexWrap: 'nowrap',
                      gap: 24,
                    }
                  : {}
              }>
              <Grid
                className={classes.diagramContent}
                item={true}
                sm={12}
                md={6}
                lg={6}
                xl={6}
                style={
                  newVersion && !isMobile
                    ? {
                        display: 'flex',
                        justifyContent: 'center',
                        width: 'auto',
                        flex: 'none',
                      }
                    : {}
                }>
                {dataMapValuePourcentage && (
                  <Diagram data={dataMapValuePourcentage} newVersion={newVersion} />
                )}
              </Grid>
              <Grid
                item={true}
                sm={12}
                md={6}
                lg={6}
                xl={6}
                className={classes.labelsGrid}
                style={
                  newVersion && !isMobile
                    ? {
                        alignSelf: 'flex-start',
                        width: 'auto',
                        flex: 'none',
                      }
                    : {}
                }>
                {dataMapValuePourcentage.map((v: any, i: number) => {
                  return (
                    <div
                      key={(v.id || v.label) ?? i}
                      className={classes.labelContainer}
                      style={newVersion && !isMobile ? { paddingTop: 12 } : {}}>
                      <div
                        className={classes.labelLegend}
                        style={{ backgroundColor: colors[i], flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, minWidth: 0, marginTop: 5 }}>{v.label}</span>
                    </div>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default PieDiagramContainer;
